import React from "react";
import * as Sentry from "@sentry/react";
import { Box, Typography, Button, Paper } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

/**
 * Error boundary that catches chunk loading failures (common after deployments)
 * and provides a user-friendly way to reload the app.
 */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a chunk loading error
    const isChunkError =
      error?.message?.includes("Failed to fetch dynamically imported module") ||
      error?.message?.includes("Loading chunk") ||
      error?.message?.includes("Loading CSS chunk") ||
      error?.message?.includes("is not a valid JavaScript MIME type") ||
      error?.name === "ChunkLoadError";

    return { hasError: true, error, isChunkError };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error for debugging
    console.error("ChunkErrorBoundary caught an error:", error, errorInfo);

    // Report real crashes to Sentry (chunk errors are filtered in sentry.js beforeSend)
    if (!this.state.isChunkError) {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo?.componentStack } },
      });
    }
  }

  handleReload = () => {
    // Clear caches and do a hard reload
    if ("caches" in window) {
      caches.keys().then((names) => {
        names.forEach((name) => caches.delete(name));
      });
    }
    window.location.reload(true);
  };

  render() {
    if (this.state.hasError) {
      const { isChunkError } = this.state;

      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          p={3}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              maxWidth: 450,
              textAlign: "center",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
            }}
          >
            <ErrorOutlineIcon
              sx={{ fontSize: 48, color: "warning.main", mb: 2 }}
            />
            <Typography variant="h6" gutterBottom>
              {isChunkError
                ? "App Update Available"
                : "Something went wrong"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {isChunkError
                ? "A new version of T3 Books has been deployed. Please refresh to get the latest version."
                : "An unexpected error occurred. Please try refreshing the page."}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
              sx={{ textTransform: "none" }}
            >
              Refresh Page
            </Button>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
